import { useEffect, useState } from "react";
import { Layout } from "../components/layout.tsx";
import { useAuth } from "../context/auth.tsx";
import { listQaPlans, releaseQaPlan, type QaPlan } from "../api/delivery.ts";

export function DeliveryReleasePage() {
  const { token } = useAuth();
  const [plans, setPlans] = useState<QaPlan[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [acting, setActing] = useState<string | null>(null);

  const load = () => {
    if (token == null) return;
    setLoading(true);
    listQaPlans(token)
      .then(setPlans)
      .catch((e: unknown) => setError(e instanceof Error ? e.message : "Failed"))
      .finally(() => setLoading(false));
  };

  useEffect(load, [token]);

  const release = async (plan: QaPlan) => {
    if (token == null) return;
    if (!window.confirm(`Release QA plan "${plan.title}"? This marks all checklist items as finalized.`)) return;
    try {
      setActing(plan.id);
      await releaseQaPlan(token, plan.id, plan.version);
      load();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed");
    } finally {
      setActing(null);
    }
  };

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold text-white">Release Preparation</h1>
        </div>

        <div className="bg-yellow-900/20 border border-yellow-600/30 rounded-lg p-4">
          <p className="text-yellow-300 text-sm font-medium">Human Approval Required</p>
          <p className="text-yellow-400/70 text-sm mt-1">Production deployments require human review and approval. AI agents prepare release artifacts but cannot deploy to production.</p>
        </div>

        {loading && <p className="text-gray-400">Loading…</p>}
        {error != null && <p className="text-red-400">{error}</p>}

        <div className="space-y-4">
          {plans.map((plan) => {
            const total = plan.releaseChecklist.length;
            const done = plan.releaseChecklist.filter(i => i.done).length;
            const pct = total > 0 ? Math.round((done / total) * 100) : 0;
            return (
              <div key={plan.id} className="bg-gray-800/50 border border-gray-700 rounded-lg p-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h3 className="text-white font-semibold">{plan.title}</h3>
                    <div className="flex items-center gap-3 mt-2">
                      <div className="flex-1 bg-gray-700 rounded-full h-2">
                        <div className="bg-green-500 h-2 rounded-full transition-all" style={{ width: `${pct}%` }} />
                      </div>
                      <span className="text-gray-400 text-sm shrink-0">{done}/{total} ({pct}%)</span>
                    </div>
                    <div className="mt-3 space-y-1">
                      {plan.releaseChecklist.slice(0, 5).map((item, i) => (
                        <div key={i} className="flex items-center gap-2 text-sm">
                          <span className={item.done ? "text-green-400" : "text-gray-500"}>{item.done ? "✓" : "○"}</span>
                          <span className={item.done ? "text-gray-500 line-through" : "text-gray-300"}>{item.item}</span>
                        </div>
                      ))}
                      {plan.releaseChecklist.length > 5 && <p className="text-gray-500 text-xs">+{plan.releaseChecklist.length - 5} more items</p>}
                    </div>
                  </div>
                  {plan.status !== "RELEASED" && (
                    <button
                      disabled={acting != null || pct < 100}
                      onClick={() => release(plan)}
                      className="ml-4 px-4 py-2 text-sm bg-green-700 hover:bg-green-600 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed shrink-0"
                      title={pct < 100 ? "Complete all checklist items first" : "Mark as released"}
                    >
                      Release
                    </button>
                  )}
                  {plan.status === "RELEASED" && <span className="ml-4 px-4 py-2 text-sm bg-green-900/50 text-green-300 rounded-lg shrink-0">Released</span>}
                </div>
              </div>
            );
          })}
          {plans.length === 0 && !loading && (
            <div className="bg-gray-800/30 border border-gray-700/50 rounded-lg p-8 text-center">
              <p className="text-gray-500">No QA plans found. Create a QA plan in the QA section to track release readiness.</p>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}
